import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import * as Papa from "papaparse";

import { channelNameToType } from "../const";
import Input from "../form_components/Input";
import Button from "../form_components/Button";

const MAX_ROWS_TO_DISPLAY = 200;
const DEFAULT_FILE_NAME = 'Untitled_CSV.csv';
const CHANNEL_LAYER_HEADER = 'Channel Layer';
const CHANNEL_NOT_FOUND_ERROR = 'Channel not found';
const DEFAULT_CHANNEL = null;
const DEFAULT_CSV_FILE = { data: [], errors: [], meta: {}};
const CSVUploadButtonAndPreview = ({
  onSelectFile,
  onSelectChannel,
  shouldRenderFileName,
  shouldRenderChannelInfo,
  previewStyles,
  validateRowFunction,
  onDetectInvalidRows,
  onSubmit,
  onClose,
}) => {
  const [uploadedFile, setUploadedFile] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState(DEFAULT_FILE_NAME);
  const [csvFile, setCsvFile] = useState(DEFAULT_CSV_FILE);
  const [channel, setChannel] = useState(DEFAULT_CHANNEL);
  const [shouldRenderFooter, setShouldRenderFooter] = useState(false);
  const [hasRowErrors, setHasRowErrors] = useState(false);
  const [showWrongChannelError, setShowWrongChannelError] = useState(false);
  const inputFileRef = useRef();
  
  useEffect(() => {
    if (!uploadedFile) {
      return;
    }

    const csv = getParsedFile(uploadedFile);

    if (csv.data.length === 0){
      return;
    }

    // Clean up empty or falsy value from header.
    // This will speed up the file processing on big CSV files.
    // Filter out the Channel Layer header.
    csv.meta.fields = csv.meta.fields.filter(x => !!x && x !== CHANNEL_LAYER_HEADER);
    const [channelLayer, cleanedCsvData] = extractAndCleanChannelLayer(csv.data);
    const isValidChannel = !!channelNameToType[channelLayer];
    const channelValue = isValidChannel? channelLayer: DEFAULT_CHANNEL;

    setChannel(channelValue);
    onSelectChannel(channelValue);
    setShowWrongChannelError(channelLayer && !isValidChannel);

    csv.data = cleanedCsvData;

    setCsvFile(csv);

  }, [uploadedFile]);

  useEffect(() => {
    return () => clearUploadedFile();
  }, []);

  /**
   * Returns the parsed version of the passed in CSV string content.
   * @param {string} csvString A string of delimited text to be parsed.
   * @returns The parsed CSV file.
   */
  const getParsedFile = (csvString) => {
    return Papa.parse(csvString, { header: true });
  };

  /**
   * Handler for selecting a file from the input file.
   * @param {Event} event
   */
  const onFileSelect = (event) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    // The uploaded file is not a CSV.
    if (file.type !== 'text/csv') {
      setUploadedFileName(DEFAULT_FILE_NAME);
      onSelectFile(false);
      clearUploadedFile();
      return;
    }

    // Read the uploaded CSV.
    const reader = new FileReader();
    reader.onload = (e) => {
      const fileResult = e.target.result;
      setUploadedFile(fileResult);
      setUploadedFileName(file.name);
      onSelectFile(true, getParsedFile(fileResult), fileResult);
    };
    reader.readAsText(file);
  };

  /**
   * Clear the current uploaded file.
   */
  const clearUploadedFile = () => {
    setUploadedFile('');
    setCsvFile(DEFAULT_CSV_FILE);
    if (inputFileRef.current) {
      inputFileRef.current.value = null;
    }
  };

  /**
   * Handler for the submit button.
   */
  const onSubmitHandler = () => {
    const csv = getParsedFile(uploadedFile);
    if (csv.data.length === 0) {
      return;
    }

    onSubmit();
    clearUploadedFile();
  };

  /**
   * Handler for the close button.
   */
  const onCloseHandler = () => {
    clearUploadedFile();
    onClose();
  };

  /**
   * Get the headers to be rendered on the preview.
   * @param {string[]} headerData The headers of the CSV file.
   * @returns {JSX.Element}
   */
  const getRenderedHeaders = () => {
    const headerData = csvFile.meta.fields;
    return headerData.map((x, i) => (
      <th key={i}>{x}</th>
    ));
  };

  /**
   * Get the rows (data) to be rendered on the preview.
   * @returns {Array<JSX.Element>}
   */
  const getRenderedRowsMemoized = useMemo(() => {
    const headers = csvFile.meta.fields
    const csvData = csvFile.data

    // Render up to MAX_ROWS_TO_DISPLAY rows,
    // if the file has more than MAX_ROWS_TO_DISPLAY rows
    // then we display a message indicating that there are more.
    let hasError = false;
    const rows = [];
    const maxRows = Math.min(MAX_ROWS_TO_DISPLAY, csvData.length);

    for (let i = 0; i < maxRows; i++) {
      // Safety check to make sure that the current row
      // indeed has the proper headers.
      if (Object.keys(csvData[i]).length <= 1) {
        continue;
      }

      const cells = [];
      headers.forEach((header, j) => {
        const rowValue = csvData[i];

        // If the validator function is passed in as a props
        // then we execute it against each row on the parsed file.
        let validatedData = {
          value: rowValue[header],
          class: 'black',
        };
        if (typeof validateRowFunction === 'function') {
          validatedData = validateRowFunction(rowValue, header);
        }

        if (!!validatedData && Object.keys(validatedData).length > 0) {
          if (validatedData.class === 'red') {
            hasError = true;
          }

          // Assigned the cleaned up version of the row value, this is shown on the UI/preview.
          rowValue[header] = validatedData.value;
          cells.push(<td style={{ color: validatedData.class }} key={`${i}-${j}`}>{rowValue[header]}</td>);
        }
      });

      rows.push(<tr key={i}>{cells}</tr>);
    }

    setHasRowErrors(hasError);

    setShouldRenderFooter(
      rows.length === MAX_ROWS_TO_DISPLAY && csvData.length > MAX_ROWS_TO_DISPLAY
    );
  
    return rows

  }, [csvFile])

  /**
   * Get the channel layer from the CSV data.
   * @param {object[]} csvData The data (rows) of the CSV file
   * @returns {Array<string, object[]>} The first item is the channel layer and the second item is the CSV data without the channel layer.
    */
  const extractAndCleanChannelLayer = (csvData) => {
    if (csvData.length === 0) {
      return ['', csvData];
    }
    const channelLayer = csvData[0][CHANNEL_LAYER_HEADER];
    const cleanedCsvData = csvData.map((row) => {
      delete row[CHANNEL_LAYER_HEADER];
      return row;
    });

    return [channelLayer, cleanedCsvData];
  };

  // Notify the parent component that at least one row has errors
  // based on the validations performed on the `validateRowFunction` parameter.
  if (hasRowErrors && uploadedFile !== '' && typeof onDetectInvalidRows === 'function') {
    onDetectInvalidRows();
  }

  const shouldRenderButtonsContainer = typeof onSubmit === 'function' || typeof onClose === 'function';

  if (showWrongChannelError && shouldRenderChannelInfo) {
    onSelectFile(false);
  }

  return (
    <>

      <div className="row">
        <div className={shouldRenderChannelInfo && (channel || showWrongChannelError) ? "col-md-8": "col-md-12"}>
          {shouldRenderFileName ? (
            <div style={{ fontWeight: "normal", display: "block", width: "100%" }}>
              <Input
                label={window.l.popup.csv_name}
                context={{ getConfig: () => uploadedFileName }}
                value={uploadedFileName}
                valRules={{ noEmpty: true }}
                readonly
              />
            </div>
          ) : null}
        </div>
        <div className="col-md-4">
          {shouldRenderChannelInfo && (channel || showWrongChannelError) &&
              <Input
                label={window.l.popup.channel_layer}
                context={{ getConfig: () => channel || CHANNEL_NOT_FOUND_ERROR }}
                valRules={{ noEmpty: true }}
                value={channel || CHANNEL_NOT_FOUND_ERROR }
                readonly
              />
            }
        </div>
      </div>
      <input
        type="file"
        ref={inputFileRef}
        style={{ margin: "10px 0" }}
        accept=".csv"
        onChange={onFileSelect}
      />
      <div style={previewStyles}>
        {csvFile.data.length ? (
          <table
            style={{ marginBottom: 0 }}
            className="summary-table table table-condensed table-responsive"
          >
            <thead>
              <tr>{getRenderedHeaders()}</tr>
            </thead>
            <tbody>{getRenderedRowsMemoized}</tbody>
            <tfoot>
              <tr>{shouldRenderFooter && 
                  <td colSpan={3}>
                      <span><b>... {`${window.l.form.and_space} ${csvFile.data.length - MAX_ROWS_TO_DISPLAY} ${window.l.form.space_more_rows}`}</b></span>
                  </td>
              }</tr>
            </tfoot>
          </table>
        ) : null}
      </div>
      {shouldRenderButtonsContainer ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '30px',
          }}
        >
          {(uploadedFile !== '' && !hasRowErrors && typeof onSubmit === 'function') &&
            <Button
              label={window.l.form.submit}
              wide={false}
              callback={onSubmitHandler}
            />
          }
          &nbsp;&nbsp;
          {(uploadedFile !== '' && typeof onClose === 'function') &&
            <Button
              label={window.l.form.close}
              wide={false}
              tint="secondary"
              callback={onCloseHandler}
            />
          }
        </div>
      ) : null}
    </>
  );
};

CSVUploadButtonAndPreview.propTypes = {
  onSelectFile: PropTypes.func.isRequired,
  onSelectChannel: PropTypes.func,
  shouldRenderFileName: PropTypes.bool,
  shouldRenderChannelInfo: PropTypes.bool,
  previewStyles: PropTypes.object,
  validateRowFunction: PropTypes.func,
  onDetectInvalidRows: PropTypes.func,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
};

CSVUploadButtonAndPreview.defaultProps = {
  previewStyles: {},
  shouldRenderFileName: false,
  shouldRenderChannelInfo: false,
  onSelectChannel: () => {},
};

export default CSVUploadButtonAndPreview;
